"use client"

import InitialLoadingScreen from "@/components/ui/InitialLoadingScreen"
import { useUser } from "@/contexts/UserContext"
import { usePathname, useRouter } from "next/navigation"
import { useEffect, useState } from "react"

export default function ProtectedGate({
  children,
}: {
  children: React.ReactNode
}) {
  const router = useRouter()
  const pathname = usePathname()
  const { claimed, _raw } = useUser()
  const [authChecked, setAuthChecked] = useState(false)
  const [isChecking, setIsChecking] = useState(true)

  // Vérifier si on est sur une route publique qui ne nécessite pas de vérification
  const isPublicAuthRoute =
    pathname.startsWith("/auth/") ||
    pathname === "/signin" ||
    pathname === "/signup"

  // Vérifier l'authentification et le claimStatus côté client
  useEffect(() => {
    // Si on est sur une route publique d'auth, ne pas vérifier
    if (isPublicAuthRoute) {
      setAuthChecked(true)
      setIsChecking(false)
      return
    }

    let cancelled = false
    let timeoutId: NodeJS.Timeout | null = null

    const checkAuthAndClaimStatus = async () => {
      // Optimisation: L'authentification est déjà vérifiée côté serveur dans ProtectedLayout
      // via getUserEmail() qui redirige vers /signin si non authentifié.
      // On peut donc directement vérifier le claimStatus depuis UserContext sans appeler /api/me.

      // Attendre que _raw soit disponible (peut prendre un peu de temps au premier rendu)
      // On attend maximum 3 secondes pour que les données du UserProvider soient disponibles
      const maxWaitTime = 3000
      const startTime = Date.now()

      while (!_raw && Date.now() - startTime < maxWaitTime && !cancelled) {
        await new Promise((resolve) => setTimeout(resolve, 100))
      }

      // Si _raw n'est toujours pas disponible après l'attente initiale,
      // cela signifie que le UserProvider n'a pas encore initialisé
      // Dans ce cas, on ne peut pas vérifier le claimStatus, donc on débloque pour éviter le blocage
      // Le UserProvider devrait toujours fournir _raw (même avec des valeurs null)
      if (_raw === undefined) {
        console.warn(
          "[ProtectedGate] _raw is undefined - UserProvider may not be initialized yet"
        )
        // Attendre encore un peu pour voir si _raw devient disponible
        await new Promise((resolve) => setTimeout(resolve, 1000))
        // Si toujours undefined après cette attente, considérer comme une erreur de configuration
        if (_raw === undefined && !cancelled) {
          console.error(
            "[ProtectedGate] _raw still undefined after wait - UserProvider may be missing"
          )
          // Débloquer pour éviter le blocage infini
          setAuthChecked(true)
          setIsChecking(false)
          return
        }
      }

      // Maintenant _raw devrait être défini (soit un objet, soit toujours undefined en cas d'erreur)
      const claimStatus = _raw?.claimStatus
      const identity = _raw?.identity

      // ════════════════════════════════════════════════════════════════
      // REDIRECTIONS CONNECT DÉSACTIVÉES - Auth Silencieuse Classique
      // ════════════════════════════════════════════════════════════════
      // Les vérifications identity et claimStatus sont commentées pour permettre
      // l'accès direct après authentification Azure B2C, sans passage par Connect
      /*
      // Vérifier que le contact existe dans Drupal
      // identity peut être null si aucun contact n'a été trouvé (c'est différent de undefined)
      // Si identity est null, cela signifie que l'authentification serveur a réussi mais qu'aucun contact n'a été trouvé
      if (identity === null || identity === undefined) {
        // Pas de contact trouvé - rediriger vers Connect pour créer le membership
        if (!cancelled) {
          const connectUrl = new URL(
            `${process.env.NEXT_PUBLIC_ACCOUNT_APP_URL || "https://connect.uic.org"}/register`
          )
          connectUrl.searchParams.set(
            "app",
            process.env.NEXT_PUBLIC_APP_ID || "safety-hub"
          )
          connectUrl.searchParams.set(
            "return_url",
            `${window.location.origin}/auth/registration-complete`
          )
          window.location.href = connectUrl.toString()
        }
        return
      }

      // Vérifier que le claimStatus existe et est 'claimed'
      if (!claimStatus || claimStatus.status !== "claimed") {
        // L'utilisateur n'a pas encore claim son compte - rediriger vers connect.uic.org
        if (!cancelled) {
          window.location.href = `${process.env.NEXT_PUBLIC_ACCOUNT_APP_URL || "https://connect.uic.org"}?appRequested=safety-hub.uic.org`
        }
        return
      }
      */

      // ✅ Mode Auth Silencieuse: Accès direct après authentification Azure B2C
      // Les utilisateurs peuvent accéder à l'app même sans contact Drupal ou compte non-claimed
      console.log("[ProtectedGate] ✅ Auth silencieuse - Accès autorisé", {
        hasIdentity: !!identity,
        hasClaim: !!claimStatus,
        claimStatus: claimStatus?.status || "unknown",
      })

      // TODO: Vérifier l'accès spécifique à l'app quand le microservice apps sera disponible
      // Pour l'instant, si le contact existe et est claimed, on considère qu'il a accès
      // Plus tard: vérifier membership.apps?.includes(process.env.NEXT_PUBLIC_APP_ID) ou membership.pending_apps?.includes(process.env.NEXT_PUBLIC_APP_ID)

      // Tout est OK
      if (!cancelled) {
        setAuthChecked(true)
        setIsChecking(false)
        return
      }
    }

    // Timeout de sécurité : si après 8 secondes on n'a pas résolu, débloquer
    // Ce timeout est plus court que le maxLoadingTime de LoadingManager (8s) pour éviter les conflits
    timeoutId = setTimeout(() => {
      if (!cancelled && (isChecking || !authChecked)) {
        console.warn(
          "[ProtectedGate] Timeout reached (8s), unblocking to prevent infinite loading"
        )
        setAuthChecked(true)
        setIsChecking(false)
      }
    }, 8000)

    checkAuthAndClaimStatus()

    return () => {
      cancelled = true
      if (timeoutId) {
        clearTimeout(timeoutId)
      }
    }
    // Note: isChecking and authChecked are intentionally excluded to prevent infinite loops
    // They are only used in the timeout callback which is safe
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router, pathname, _raw, isPublicAuthRoute])

  // Afficher un loader pendant la vérification d'authentification
  // Ne pas bloquer si on est sur une route publique d'auth
  if ((isChecking || !authChecked) && !isPublicAuthRoute) {
    return (
      <div className="flex items-center justify-center flex-1 min-h-screen">
        <div className="text-center">
          <InitialLoadingScreen />
        </div>
      </div>
    )
  }

  return <>{children}</>
}
